import React, { useState, useEffect, useRef } from 'react';
import '../Styles/ChatBot.css';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Hi! I'm Alex, your health assistant. How can I help you today?", sender: 'bot' }
  ]);
  const [userInput, setUserInput] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [appointmentData, setAppointmentData] = useState({
    name: '',
    mobile: '',
    problem: '',
    date: '',
    time: ''
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Auto-open chat after 4 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  const healthTips = [
    "💡 Stay hydrated! Drink at least 8 glasses of water daily for optimal brain function.",
    "🧘 Practice mindfulness meditation for 10 minutes daily to reduce stress and improve mental clarity.",
    "🏃‍♂️ Regular exercise can improve cognitive function and reduce the risk of neurological disorders.",
    "🥗 A balanced diet rich in antioxidants supports brain health and prevents neurodegenerative diseases.",
    "😴 Quality sleep is crucial for memory consolidation and overall neurological health.",
    "🧠 Engage in brain-stimulating activities like puzzles or learning new skills to maintain cognitive sharpness."
  ];

  const conversationFlow = [
    { question: "Wonderful! I'd love to help you book an appointment. May I know your full name please?", field: 'name' },
    { question: "Perfect! And what's your mobile number so we can stay in touch?", field: 'mobile' },
    { question: "Thanks for sharing that! Could you tell me what health concern brings you here today?", field: 'problem' },
    { question: "Got it! When would be a good day for your appointment? (Please share the date in DD/MM/YYYY format)", field: 'date' },
    { question: "Almost done! What time works best for you? (Please specify AM or PM)", field: 'time' }
  ];

  const sendToWhatsApp = (data) => {
    const message = `New Appointment Booking:\n\nName: ${data.name}\nMobile: ${data.mobile}\nProblem: ${data.problem}\nDate: ${data.date}\nTime: ${data.time}`;
    const whatsappUrl = `https://wa.me/8374062188?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleSendMessage = () => {
    if (!userInput.trim()) return;

    const newMessages = [...messages, { text: userInput, sender: 'user' }];
    setMessages(newMessages);
    setUserInput('');

    // Simulate bot thinking with faster response for smoother conversation
    setTimeout(() => {
      let botResponse = '';

      if (currentStep < conversationFlow.length) {
        // Update appointment data
        setAppointmentData(prev => ({
          ...prev,
          [conversationFlow[currentStep].field]: userInput
        }));

        // Move to next step
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);

        if (nextStep < conversationFlow.length) {
          botResponse = conversationFlow[nextStep].question;
          // Add a health tip occasionally for friendly conversation
          if (Math.random() > 0.6) {
            const randomTip = healthTips[Math.floor(Math.random() * healthTips.length)];
            botResponse += `\n\n${randomTip}`;
          }
        } else {
          // Appointment booking complete - direct and friendly
          botResponse = `Perfect! I've got everything I need. Let me send your appointment details to our clinic right away.\n\n📋 Appointment Summary:\nName: ${appointmentData.name || userInput}\nMobile: ${appointmentData.mobile}\nConcern: ${appointmentData.problem}\nDate: ${appointmentData.date}\nTime: ${appointmentData.time}\n\nSending to WhatsApp now...`;

          // Send to WhatsApp immediately for direct response
          const completeData = {
            ...appointmentData,
            [conversationFlow[currentStep].field]: userInput
          };
          setTimeout(() => sendToWhatsApp(completeData), 1000);
        }
      } else {
        // General conversation - more direct and friendly
        const responses = [
          "I'd be happy to help you book an appointment. Shall we get started?",
          "I'm here to assist with your health needs. Would you like to schedule a consultation?",
          "Feel free to ask me anything about our neurological services!",
          "How can I help you with your health concerns today?"
        ];
        botResponse = responses[Math.floor(Math.random() * responses.length)];

        // Add health tip more frequently for friendly interaction
        if (Math.random() > 0.3) {
          const randomTip = healthTips[Math.floor(Math.random() * healthTips.length)];
          botResponse += `\n\n${randomTip}`;
        }
      }

      setMessages(prev => [...prev, { text: botResponse, sender: 'bot' }]);
    }, 800); // Faster response time for smoother conversation
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Chat Bot Toggle Button */}
      <div className="chatbot-toggle" onClick={toggleChat}>
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="white"/>
          </svg>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C13.1 2 14 2.9 14 4V5H16C17.1 5 18 5.9 18 7V12C18 13.1 17.1 14 16 14H14V15C14 16.1 13.1 17 12 17C10.9 17 10 16.1 10 15V14H8C6.9 14 6 13.1 6 12V7C6 5.9 6.9 5 8 5H10V4C10 2.9 10.9 2 12 2ZM12 4C11.45 4 11 4.45 11 5V6H13V5C13 4.45 12.55 4 12 4ZM8 7V12H16V7H8Z" fill="white"/>
            <path d="M20 18H4C3.45 18 3 18.45 3 19C3 19.55 3.45 20 4 20H20C20.55 20 21 19.55 21 19C21 18.45 20.55 18 20 18Z" fill="white"/>
            <circle cx="9" cy="10" r="1" fill="white"/>
            <circle cx="12" cy="10" r="1" fill="white"/>
            <circle cx="15" cy="10" r="1" fill="white"/>
          </svg>
        )}
      </div>

      {/* Chat Bot Window */}
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="bot-avatar">
              <span>🤖</span>
              <div className="whatsapp-icon" onClick={() => window.open('https://wa.me/8374062188', '_blank')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" fill="#25D366"/>
                </svg>
              </div>
            </div>
            <div className="bot-info">
              <h4>Alex</h4>
              <span className="status">Online</span>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.sender}`}>
                <div className="message-content">
                  {message.text.split('\n').map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <input
              type="text"
              placeholder="Type your message..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button onClick={handleSendMessage}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="white"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
